import { FieldArray, Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import type { Condition, Event, StateMachineState, Transition } from '../../ProgramStateMachine.types'
import { useStateMachineEditorContext } from '../../context/StateMachineEditorContext'
import { CardRow } from '../CardRow'

import { Button, DateInput, Flex, FormField, TextInput, Select, Typography } from '@percent/lemonade'
import { parseConditionValue } from '@percent/admin-dashboard/app/grants/utils/utils'

export interface TransitionEditFormProps {
  onSubmit: (transition: Transition) => void
  onCancel?: () => void
  availableStates: StateMachineState[]
  initialValues?: Transition
}

interface EventFormValues {
  type: string
  logicalOperator: string
  conditions: { field: string; operator: string; value: string }[]
}

interface FormValues {
  targetState: string
  timeGreaterThan: number | null
  logicalOperator: string
  events: EventFormValues[]
}

function parseDateToTimestamp(value?: string | number): number | null {
  if (value === undefined || value === null) return null

  if (typeof value === 'number') return Number.isNaN(value) ? null : value
  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? null : date.getTime()
}

function toFormValues(transition?: Transition): FormValues {
  if (!transition) {
    return {
      targetState: '',
      timeGreaterThan: null,
      logicalOperator: 'and',
      events: [{ type: '', logicalOperator: 'and', conditions: [] }]
    }
  }

  return {
    targetState: transition.to,
    timeGreaterThan: parseDateToTimestamp(transition.when?.timeGreaterThan),
    logicalOperator: transition.when?.eventsMatching?.logicalOperator ?? 'and',
    events: (transition.when?.eventsMatching?.events ?? []).map(evt => ({
      type: evt.type,
      logicalOperator: evt.payload?.logicalOperator ?? 'and',
      conditions: (evt.payload?.conditions ?? []).map(c => ({
        field: c.field,
        operator: c.operator,
        value: String(c.value)
      }))
    }))
  }
}

function fromFormValues(values: FormValues): Transition {
  const events: Event[] = values.events
    .filter(e => e.type.trim())
    .map(e => {
      const conditions: Condition[] = e.conditions
        .filter(c => c.field.trim())
        .map(c => ({
          field: c.field,
          operator: c.operator,
          value: parseConditionValue(c.value)
        }))

      const event: Event = { type: e.type }

      if (conditions.length > 0) {
        event.payload = {
          logicalOperator: e.logicalOperator,
          conditions
        }
      }

      return event
    })

  const transition: Transition = {
    to: values.targetState,
    when: {
      eventsMatching: {
        logicalOperator: values.logicalOperator,
        events
      }
    }
  }

  if (values.timeGreaterThan) {
    transition.when.timeGreaterThan = values.timeGreaterThan
  }

  return transition
}

export function TransitionEditForm({
  onSubmit,
  onCancel,
  availableStates,
  initialValues
}: Readonly<TransitionEditFormProps>) {
  const { t } = useTranslation()
  const { metadata } = useStateMachineEditorContext()
  const isEditMode = !!initialValues

  const eventNameOptions = metadata.eventNames

  const operatorOptions = metadata.comparisonOperators.map(op => ({
    value: op,
    label: op
  }))

  const logicalOperatorOptions = metadata.logicalOperators.map(op => ({
    value: op,
    label: op.toUpperCase()
  }))

  const { eventPayloadFields } = metadata

  const taskTemplateOptions = metadata.taskTemplates.map(tmpl => ({
    value: tmpl.id,
    label: tmpl.name
  }))

  const outcomeOptions = [
    { value: 'Pass', label: 'Pass' },
    { value: 'Fail', label: 'Fail' }
  ]

  const hasDecisionTemplate = (event: EventFormValues) => {
    const templateCondition = event.conditions.find(c => c.field === 'taskTemplateId')

    if (!templateCondition) return false

    const template = metadata.taskTemplates.find(tmpl => tmpl.id === templateCondition.value)

    return template?.details?.type === 'decision'
  }

  const getFieldOptions = (event: EventFormValues) => {
    const baseFields = eventPayloadFields[event.type] ?? []
    const fields = hasDecisionTemplate(event)
      ? [...baseFields, ...(baseFields.some(f => f.field === 'outcome') ? [] : [{ field: 'outcome', type: 'string' }])]
      : baseFields.filter(f => f.field !== 'outcome')

    return fields
  }

  const stateOptions = availableStates.map(s => ({
    value: s.id,
    label: s.metadata?.displayKey || s.id
  }))

  const validate = (values: FormValues) => {
    const errors: Record<string, string> = {}

    if (!values.targetState) {
      errors.targetState = t('stateMachine.transitionEdit.validation.targetRequired')
    }

    return errors
  }

  return (
    <Formik<FormValues>
      initialValues={toFormValues(initialValues)}
      validate={validate}
      onSubmit={values => {
        onSubmit(fromFormValues(values))
      }}
      enableReinitialize
    >
      {({ values, errors, touched, handleChange, handleBlur, setFieldValue, submitForm }) => (
        <Flex direction="column" flex={1} miw={0} mih={0}>
          <Flex direction="column" gap="md" flex={1} mih={0} pb={8} style={{ overflowY: 'auto' }}>
            <FormField
              label={t('stateMachine.transitionEdit.targetState')}
              status={touched.targetState && errors.targetState ? 'danger' : 'default'}
              statusMessage={touched.targetState && errors.targetState ? errors.targetState : ''}
              necessity="required"
            >
              <Select
                name="targetState"
                options={stateOptions}
                defaultValue={stateOptions.find(o => o.value === values.targetState)}
                onChange={option => setFieldValue('targetState', option?.value ?? '')}
              />
            </FormField>

            <FormField label={t('stateMachine.transitionEdit.timeGreaterThan')}>
              <DateInput
                name="timeGreaterThan"
                value={values.timeGreaterThan ? new Date(values.timeGreaterThan) : null}
                onChange={date => setFieldValue('timeGreaterThan', date ? date.getTime() : null)}
                onBlur={handleBlur}
                clearable
              />
            </FormField>

            <FormField label={t('stateMachine.transitionEdit.logicalOperator')}>
              <Select
                name="logicalOperator"
                options={logicalOperatorOptions}
                defaultValue={
                  logicalOperatorOptions.find(o => o.value === values.logicalOperator) ?? logicalOperatorOptions[0]
                }
                onChange={option => setFieldValue('logicalOperator', option?.value ?? 'and')}
              />
            </FormField>

            <Flex direction="column" gap="s">
              <Typography variant="h4">{t('stateMachine.transitionEdit.events')}</Typography>
              <FieldArray name="events">
                {({ push: pushEvent, remove: removeEvent }) => (
                  <Flex direction="column" gap="md">
                    {values.events.map((event, eventIndex) => (
                      <CardRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={eventIndex}
                        onRemove={values.events.length > 1 ? () => removeEvent(eventIndex) : undefined}
                        removeLabel={t('button.remove')}
                        removeTestId={`remove-event-${eventIndex}`}
                        testId={`event-block-${eventIndex}`}
                      >
                        <Flex direction="column" gap="xs">
                          <FormField label={t('stateMachine.transitionEdit.eventType')}>
                            {eventNameOptions.length > 0 ? (
                              <Select
                                name={`events.${eventIndex}.type`}
                                options={eventNameOptions}
                                defaultValue={eventNameOptions.find(o => o.value === event.type)}
                                onChange={option => setFieldValue(`events.${eventIndex}.type`, option?.value ?? '')}
                                placeholder={t('stateMachine.transitionEdit.eventNamePlaceholder')}
                              />
                            ) : (
                              <TextInput
                                name={`events.${eventIndex}.type`}
                                value={event.type}
                                onChange={handleChange}
                                placeholder={t('stateMachine.transitionEdit.eventNamePlaceholder')}
                              />
                            )}
                          </FormField>

                          <FieldArray name={`events.${eventIndex}.conditions`}>
                            {({ push: pushCondition, remove: removeCondition }) => (
                              <Flex
                                direction="column"
                                gap="xs"
                                style={{ paddingLeft: 12, borderLeft: '2px solid #e2e8f0' }}
                              >
                                {event.conditions.length > 0 && (
                                  <FormField label={t('stateMachine.transitionEdit.conditionOperator')}>
                                    <Select
                                      name={`events.${eventIndex}.logicalOperator`}
                                      options={logicalOperatorOptions}
                                      defaultValue={
                                        logicalOperatorOptions.find(o => o.value === event.logicalOperator) ??
                                        logicalOperatorOptions[0]
                                      }
                                      onChange={option =>
                                        setFieldValue(`events.${eventIndex}.logicalOperator`, option?.value ?? 'and')
                                      }
                                    />
                                  </FormField>
                                )}
                                {event.conditions.map((condition, condIndex) => (
                                  <CardRow
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={condIndex}
                                    onRemove={() => removeCondition(condIndex)}
                                    removeLabel={t('button.remove')}
                                    removeTestId={`remove-condition-${eventIndex}-${condIndex}`}
                                    testId={`condition-${eventIndex}-${condIndex}`}
                                  >
                                    <Flex wrap="wrap" gap="xxs" align="flex-end" style={{ minWidth: 0 }}>
                                      <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                                        <FormField label={t('stateMachine.transitionEdit.field')}>
                                          {(() => {
                                            const fieldOptions = getFieldOptions(event)

                                            return fieldOptions.length > 0 ? (
                                              <Select
                                                name={`events.${eventIndex}.conditions.${condIndex}.field`}
                                                options={fieldOptions.map(f => ({
                                                  value: f.field,
                                                  label: f.field
                                                }))}
                                                defaultValue={fieldOptions
                                                  .map(f => ({ value: f.field, label: f.field }))
                                                  .find(o => o.value === condition.field)}
                                                onChange={option => {
                                                  setFieldValue(
                                                    `events.${eventIndex}.conditions.${condIndex}.field`,
                                                    option?.value ?? ''
                                                  )

                                                  const fieldType = fieldOptions.find(
                                                    f => f.field === option?.value
                                                  )?.type

                                                  if (fieldType === 'string') {
                                                    setFieldValue(
                                                      `events.${eventIndex}.conditions.${condIndex}.operator`,
                                                      '=='
                                                    )
                                                  }
                                                }}
                                                placeholder={t('stateMachine.transitionEdit.fieldPlaceholder')}
                                              />
                                            ) : (
                                              <TextInput
                                                name={`events.${eventIndex}.conditions.${condIndex}.field`}
                                                value={condition.field}
                                                onChange={handleChange}
                                                placeholder={t('stateMachine.transitionEdit.fieldPlaceholder')}
                                              />
                                            )
                                          })()}
                                        </FormField>
                                      </div>
                                      {getFieldOptions(event).find(f => f.field === condition.field)?.type !==
                                        'string' && (
                                        <div style={{ flex: '0 1 100px', minWidth: 0 }}>
                                          <FormField label={t('stateMachine.transitionEdit.operator')}>
                                            <Select
                                              name={`events.${eventIndex}.conditions.${condIndex}.operator`}
                                              options={operatorOptions}
                                              defaultValue={
                                                operatorOptions.find(o => o.value === condition.operator) ??
                                                operatorOptions[0]
                                              }
                                              onChange={option =>
                                                setFieldValue(
                                                  `events.${eventIndex}.conditions.${condIndex}.operator`,
                                                  option?.value ?? '=='
                                                )
                                              }
                                            />
                                          </FormField>
                                        </div>
                                      )}
                                      <div style={{ flex: '1 1 120px', minWidth: 0 }}>
                                        <FormField label={t('stateMachine.transitionEdit.value')}>
                                          {condition.field === 'taskTemplateId' && taskTemplateOptions.length > 0 ? (
                                            <Select
                                              name={`events.${eventIndex}.conditions.${condIndex}.value`}
                                              options={taskTemplateOptions}
                                              defaultValue={taskTemplateOptions.find(o => o.value === condition.value)}
                                              onChange={option =>
                                                setFieldValue(
                                                  `events.${eventIndex}.conditions.${condIndex}.value`,
                                                  option?.value ?? ''
                                                )
                                              }
                                              placeholder={t('stateMachine.transitionEdit.valuePlaceholder')}
                                            />
                                          ) : condition.field === 'outcome' && hasDecisionTemplate(event) ? (
                                            <Select
                                              name={`events.${eventIndex}.conditions.${condIndex}.value`}
                                              options={outcomeOptions}
                                              defaultValue={outcomeOptions.find(o => o.value === condition.value)}
                                              onChange={option =>
                                                setFieldValue(
                                                  `events.${eventIndex}.conditions.${condIndex}.value`,
                                                  option?.value ?? ''
                                                )
                                              }
                                              placeholder={t('stateMachine.transitionEdit.valuePlaceholder')}
                                            />
                                          ) : (
                                            <TextInput
                                              name={`events.${eventIndex}.conditions.${condIndex}.value`}
                                              value={condition.value}
                                              onChange={handleChange}
                                              placeholder={t('stateMachine.transitionEdit.valuePlaceholder')}
                                            />
                                          )}
                                        </FormField>
                                      </div>
                                    </Flex>
                                  </CardRow>
                                ))}
                                <Button
                                  variant="secondary"
                                  size="small"
                                  onClick={() => pushCondition({ field: '', operator: '==', value: '' })}
                                  data-testid={`add-condition-${eventIndex}`}
                                >
                                  {t('stateMachine.transitionEdit.addCondition')}
                                </Button>
                              </Flex>
                            )}
                          </FieldArray>
                        </Flex>
                      </CardRow>
                    ))}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => pushEvent({ type: '', logicalOperator: 'and', conditions: [] })}
                      data-testid="add-event"
                    >
                      {t('stateMachine.transitionEdit.addEvent')}
                    </Button>
                  </Flex>
                )}
              </FieldArray>
              {touched.events && errors.events && typeof errors.events === 'string' && (
                <Typography style={{ color: '#ef4444', fontSize: 12 }}>{errors.events}</Typography>
              )}
            </Flex>
          </Flex>

          <Flex
            gap="xs"
            justify="flex-end"
            pt={12}
            style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0' }}
            data-testid="transition-edit-footer"
          >
            {onCancel && (
              <Button variant="secondary" onClick={onCancel} data-testid="transition-edit-cancel">
                {t('button.cancel')}
              </Button>
            )}
            <Button variant="primary" onClick={submitForm} data-testid="transition-edit-submit">
              {isEditMode ? t('button.save') : t('stateMachine.transitionEdit.addTransition')}
            </Button>
          </Flex>
        </Flex>
      )}
    </Formik>
  )
}
